import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Plus, UserRound } from "lucide-react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import PerfilForm from "@/components/perfil/PerfilForm";
import { CuentasManager, CuentaFormDialog, cuentaLabel } from "@/components/entities/CuentasBancarias";

export function PerfilDialog({ open, onOpenChange, perfil, onChanged }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="perfil-dialog">
        <DialogHeader><DialogTitle>Completa tu perfil</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600 -mt-2">Se guarda en Mi perfil y se usará en tus próximos contratos. Lo que llevas del cuestionario no se pierde.</p>
        <PerfilForm perfil={perfil} onSaved={onChanged} submitLabel="Guardar en mi perfil" />
        <div className="pt-4 border-t border-[#E2DCD2]">
          <div className="font-heading font-bold text-slate-900 mb-2">Cuentas para recibir comisiones</div>
          {perfil?.persona?.id && (
            <CuentasManager personaId={perfil.persona.id} cuentas={perfil.cuentas || []}
                            defaultTitular={perfil.persona.nombre} onChanged={onChanged} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CuentaSelect({ cuentas, value, onChange, testId }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="h-14" data-testid={testId}><SelectValue placeholder="Elige una cuenta" /></SelectTrigger>
      <SelectContent>
        {cuentas.map((c) => <SelectItem key={c.id} value={c.id}>{cuentaLabel(c)}</SelectItem>)}
        <SelectItem value="manual" data-testid={`${testId}-manual`}>Llenar a mano (líneas en blanco)</SelectItem>
      </SelectContent>
    </Select>
  );
}

export function AsesorPerfilField({ field, value, onChange, perfil, reloadPerfil }) {
  const [open, setOpen] = useState(false);
  const cuentas = useMemo(() => perfil?.cuentas || [], [perfil]);
  useEffect(() => {
    if (!field.withCuenta || !perfil || value === "manual") return;
    if (!cuentas.length) { onChange(field.name, "manual"); return; }
    if (!value || !cuentas.some((c) => c.id === value)) {
      onChange(field.name, (cuentas.find((c) => c.es_principal) || cuentas[0]).id);
    }
  }, [field.withCuenta, field.name, cuentas, value, onChange, perfil]);
  if (!perfil) return <div className="text-sm text-slate-500">Cargando tu perfil…</div>;
  const p = perfil.persona || {};
  return (
    <div className="space-y-3" data-testid="asesor-perfil-field">
      <div className="rounded-xl border border-[#E2DCD2] bg-[#FBF9F5] p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center shrink-0"><UserRound className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-slate-900" data-testid="asesor-perfil-nombre">{p.nombre || "Sin nombre"}</div>
          <div className="text-xs text-slate-500 truncate">{[p.rfc || p.rfc_moral ? `RFC ${p.rfc || p.rfc_moral}` : null, p.correo, p.whatsapp].filter(Boolean).join(" · ")}</div>
          {perfil.completo ? (
            <div className="mt-2 text-xs font-semibold text-[#047857] inline-flex items-center gap-1" data-testid="asesor-perfil-completo"><CheckCircle2 className="w-3.5 h-3.5" /> Perfil completo</div>
          ) : (
            <div className="mt-2 rounded-lg bg-[#FEF3C7] border border-[#FCD34D] px-3 py-2 text-xs text-[#92400E]" data-testid="asesor-perfil-incompleto">
              <div className="font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Tu perfil está incompleto</div>
              <div className="mt-0.5">Falta: {perfil.faltantes.join(", ")}</div>
            </div>
          )}
        </div>
      </div>
      <button type="button" onClick={() => setOpen(true)} data-testid="asesor-perfil-completar-btn"
              className="text-sm text-[#1E3A5F] font-semibold hover:underline">
        {perfil.completo ? "Revisar mis datos" : "Completar mi perfil aquí"}
      </button>
      {field.withCuenta && (
        <>
          <CuentaSelect cuentas={cuentas} value={value} onChange={(v) => onChange(field.name, v)} testId={`field-${field.name}`} />
          {value === "manual" && <p className="text-xs text-slate-500" data-testid="cuenta-manual-nota">El documento saldrá con líneas en blanco (CLABE: ____________________) para llenarlas a mano o en Word.</p>}
        </>
      )}
      <PerfilDialog open={open} onOpenChange={setOpen} perfil={perfil} onChanged={reloadPerfil} />
    </div>
  );
}

export function CuentaField({ field, value, onChange, personaId, personaNombre }) {
  const [cuentas, setCuentas] = useState([]);
  const [open, setOpen] = useState(false);
  const load = async () => {
    if (!personaId) return setCuentas([]);
    const { data } = await api.get(`/personas/${personaId}/cuentas`);
    setCuentas(data);
    if (value === "manual") return;
    if (!data.length) { onChange(field.name, "manual"); return; }
    if (!data.some((c) => c.id === value)) {
      onChange(field.name, (data.find((c) => c.es_principal) || data[0]).id);
    }
  };
  useEffect(() => { load(); }, [personaId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!personaId) {
    return <p className="text-sm text-slate-500" data-testid={`field-${field.name}-sin-persona`}>Primero elige a la persona titular en el paso anterior.</p>;
  }
  return (
    <div className="space-y-2">
      <CuentaSelect cuentas={cuentas} value={value} onChange={(v) => onChange(field.name, v)} testId={`field-${field.name}`} />
      {!cuentas.length && <p className="text-sm text-slate-500" data-testid={`field-${field.name}-vacio`}>{personaNombre || "Esta persona"} aún no tiene cuentas; puedes dejarla para llenar a mano.</p>}
      <button type="button" onClick={() => setOpen(true)} data-testid={`field-${field.name}-new`}
              className="text-sm text-[#1E3A5F] font-semibold hover:underline inline-flex items-center gap-1">
        <Plus className="w-4 h-4" /> Agregar cuenta bancaria
      </button>
      <CuentaFormDialog open={open} onOpenChange={setOpen} personaId={personaId} defaultTitular={personaNombre}
                        onSaved={(c) => { onChange(field.name, c.id); load(); }} />
    </div>
  );
}
